'use client';
import { env } from '@ria-client/env';
import { AnalyticsBrowser, AnalyticsBrowserSettings, InitOptions } from '@segment/analytics-next';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useConstant } from '../hooks/useConstant';
import { SegmentAbstraction, SegmentLive, SegmentMock } from './lib/Segment';

/**
 * Analytics Context
 */
export interface AnalyticsContextValue {
  analytics?: SegmentAbstraction;
  deployableName: string;
  deployableVersion: string;
  deviceId?: string;
  gitSha: string;
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined);

/**
 * Analytics Provider
 */

export interface AnalyticsProviderProps {
  deployableName: string;
  deployableVersion: string;
  writeKey?: string;
  debug?: boolean;
  useNwCdn?: boolean;
}

export const AnalyticsProvider = ({
  deployableName,
  deployableVersion,
  writeKey,
  debug = false,
  useNwCdn = false,
  children,
}: PropsWithChildren<AnalyticsProviderProps>) => {
  const [deviceId, setDeviceId] = useState<string>();
  const analytics = useConstant(() => {
    if (debug) {
      return new SegmentMock();
    }
    if (writeKey) {
      const settings: AnalyticsBrowserSettings = {
        writeKey,
        cdnURL: 'https://siteusagetools.nerdwallet.com',
      };
      const options: InitOptions = {
        integrations: {
          'Segment.io': {
            apiHost: 'siteusage.nerdwallet.com/v1',
            protocol: 'https',
          },
        },
      };
      if (useNwCdn) {
        console.log(`LendingAnalytics: useNwCdn is deprecated, please remove it`);
      }
      const browserAnalytics = AnalyticsBrowser.load(settings, options);
      return new SegmentLive(browserAnalytics);
    }
  }, [writeKey, debug]);
  useEffect(() => {
    const cookies = new Map(
      document?.cookie.split('; ').map(cookieStr => cookieStr.split('=') as [string, string]),
    );
    if (cookies.has('cookie_id')) {
      setDeviceId(cookies.get('cookie_id'));
    } else {
      let storedCookieId = localStorage.getItem('cookie_id');
      if (!storedCookieId) {
        storedCookieId = crypto.randomUUID();
        localStorage.setItem('cookie_id', storedCookieId);
      }
      setDeviceId(storedCookieId);
    }
  }, []);
  return (
    <AnalyticsContext.Provider
      value={{
        deployableName,
        deployableVersion,
        deviceId,
        analytics,
        gitSha: env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Analytics Context Hook
 */

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics hook must used within an AnalyticsProvider');
  }
  return context;
};
